/* ============================================================================
   Doctor's Biome — product page behaviour.
   Markup: sections/drb-pdp.liquid. Paint: assets/drb-pdp.css.

   Two jobs, both small, and one rule that governs both:

   ⛔ EVERY PRICE THIS FILE DISPLAYS CAME FROM LIQUID, WHICH GOT IT FROM A
   SELLING PLAN ALLOCATION. Nothing here multiplies by 0.9. The discount lives in
   Recharge, and a percentage hard-coded in a theme is a number that goes wrong
   silently the day someone changes the plan — the page would keep displaying the
   old saving while Shopify charged the new one.
   ============================================================================ */
(() => {
  const money = (cents, sample) => {
    /* Format to match whatever Liquid's `money` filter produced, rather than
       assuming dollars — the sample is the server-rendered string. */
    const n = (cents / 100).toFixed(2);
    if (!sample) return '$' + n;
    const symbol = (sample.match(/^[^\d]*/) || [''])[0];
    const tail = (sample.match(/[^\d.,]*$/) || [''])[0];
    return symbol + n.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + tail;
  };

  /* ==========================================================================
     ⛔ REMOVE RECHARGE'S INJECTED WIDGET. THIS IS A CORRECTNESS FIX, NOT A
     COSMETIC ONE, AND CSS WOULD HAVE MADE IT WORSE.

     Recharge loads static.rechargecdn.com/assets/js/widget.min.js through a
     store-level ScriptTag, so it lands on every theme and cannot be removed from
     the theme side. It injects .rc-container-wrapper as the FIRST CHILD of the
     product form, and inside it a <select name="selling_plan">.

     That is the problem. The form then submits selling_plan TWICE — once from
     Recharge's select, once from our radio. Measured on the live preview:

         selling_plan = 2567798897   ← Recharge
         selling_plan = 2567798897   ← ours

     They agree only while both say "subscribe". Choose One-time in our card and
     ours posts empty while Recharge's still posts the plan id, so the customer
     is billed a subscription they did not pick. Hiding the widget with
     display:none would have kept that field in the form and made the bug
     invisible instead of fixing it.

     ⚠️ REMOVING THE WIDGET DOES NOT REMOVE THE SUBSCRIPTION. These are native
     Shopify selling plans; the subscription is created from the selling_plan
     line property at checkout, which our own form posts. The widget is only a
     picker, and we have replaced it with a better one.

     ⚠️ A MutationObserver RATHER THAN A ONE-OFF SWEEP, because the script is
     loaded async and re-renders on variant changes — a single pass on
     DOMContentLoaded would race it and usually lose.
     ========================================================================== */
  const stripRecharge = (form) => {
    let removed = 0;
    form.querySelectorAll('.rc-container-wrapper, .rc_container_wrapper, .rc-widget, .rc_widget').forEach((el) => {
      el.remove();
      removed++;
    });
    /* Belt and braces: anything still claiming the field name that is not ours. */
    form.querySelectorAll('[name="selling_plan"]').forEach((el) => {
      if (!el.classList.contains('drb-pdp__radio')) el.remove();
    });
    return removed;
  };

  document.querySelectorAll('[data-drb-pdp]').forEach((root) => {
    if (root.dataset.drbReady === 'true') return;
    root.dataset.drbReady = 'true';

    const form = root.querySelector('#drb-pdp-form') || root.querySelector('form[action*="/cart/add"]');
    if (form) {
      stripRecharge(form);
      new MutationObserver(() => stripRecharge(form)).observe(form, { childList: true, subtree: true });
    }

    /* ---------- gallery ---------- */
    const slides = Array.from(root.querySelectorAll('[data-drb-pdp-slide]'));
    /* ⚠️ THE INDEX COMES FROM THE ATTRIBUTE, NOT FROM THE ARRAY POSITION, and
       that is worth keeping now the dots are gone and the two happen to agree
       again. The gallery renders a lead-image override in front of the
       product's own media, so the slide a control points at has never been a
       safe thing to infer from where the control sits in a list. */
    const picks = Array.from(root.querySelectorAll('[data-drb-pdp-thumb]'));
    const pickIndex = (el) => parseInt(el.dataset.drbPdpThumb, 10);
    const stage = root.querySelector('.drb-pdp__stage');
    let index = 0;

    /* Wraps, because the gesture has no end stop the way a thumbnail does. A
       swipe that refuses at the last picture reads as broken, not as finished. */
    const wrap = (n) => ((n % slides.length) + slides.length) % slides.length;

    const showSlide = (n) => {
      if (!slides.length) return;
      index = wrap(n);
      slides.forEach((s, i) => {
        s.hidden = i !== index;
      });
      picks.forEach((el) => el.setAttribute('aria-current', pickIndex(el) === index ? 'true' : 'false'));
    };

    /* ⚠️ WARM THE HIDDEN SLIDES, BUT ONLY AFTER THE PAGE HAS FINISHED. Mike,
       2026-10-19, two thumbnails showed a broken picture with its alt text:
       every slide after the first is loading="lazy" and hidden, so a hidden
       picture is not fetched until its thumbnail is clicked, and on a slow
       connection the click shows an empty frame. Mike: "As long as it doesn't
       slow the page down in general."

       So nothing here runs before the load event, and then only when the
       browser is idle: the first picture (the LCP) and the buy box are never
       competing with it. Each slide is a ~60-120 KB WebP from the CDN. A
       visitor who has asked to save data gets no warming, only the hover and
       next-slide warming below, which fetches one picture at a time. */
    const warm = (n) => {
      const s = slides[wrap(n)];
      const img = s && s.querySelector('img[loading="lazy"]');
      if (img) img.loading = 'eager';
    };
    const saveData = !!(navigator.connection && navigator.connection.saveData);
    if (slides.length > 1 && !saveData) {
      const warmAll = () => slides.forEach((s, i) => warm(i));
      const whenIdle = () => ('requestIdleCallback' in window ? requestIdleCallback(warmAll, { timeout: 4000 }) : setTimeout(warmAll, 1500));
      if (document.readyState === 'complete') whenIdle();
      else window.addEventListener('load', whenIdle, { once: true });
    }

    picks.forEach((el) => {
      el.addEventListener('pointerenter', () => warm(pickIndex(el)), { passive: true });
      el.addEventListener('touchstart', () => warm(pickIndex(el)), { passive: true });
      el.addEventListener('click', () => { showSlide(pickIndex(el)); warm(pickIndex(el) + 1); });
    });

    /* ======================================================================
       CLICK THE SIDES, OR SWIPE. Mike, 2026-09-13: "There also needs to be
       scroller function on the image so you can left-right click on it."

       This is the hero's gesture, deliberately — same thresholds, same axis
       test, same split between pointer and touch — because the hero's version
       is the one that was argued down to something that works on a phone, and
       two different feels for the same motion on one site is worse than either.

       ⛔ NO ARROWS, and that is a choice. The thumbnail strip sits directly
       under the picture and is already a visible, clickable control, so the
       gesture is a shortcut rather than the only way through the gallery.

       ⛔ TAP-TO-ADVANCE STAYS OFF ON TOUCH. It fires on any touch that moves
       less than 10px, which is most of them — a thumb resting on the picture
       while reading the price would jump the gallery. A phone has the better
       gesture already: a swipe says next with no ambiguity.
       ====================================================================== */
    if (stage && slides.length > 1) {
      /* ⚠️ TRAVEL IS LARGER THAN COMMIT ON PURPOSE. It means the picture is
         only ever part-way across when the threshold is crossed, so the
         release finishes the motion rather than the finger completing it. */
      const INTENT = 8;
      const COMMIT = 40;
      const FLICK = 14;
      const TRAVEL = 120;
      const PEEK = 46;
      let g = null;

      stage.classList.add('is-gestured');

      const release = () => {
        stage.classList.remove('is-dragging');
        stage.style.setProperty('--drb-pdp-peek', '0px');
      };

      stage.addEventListener('pointerdown', (event) => {
        if (event.button && event.button !== 0) return;
        /* ⚠️ THE FACT BAND USED TO BE EXCLUDED HERE AND IS NOT ANY MORE. It
           covers about a third of the picture, so while it was exempt a third
           of the swipes a thumb actually makes did nothing at all — and with
           the dots gone there is no other way through the gallery. The band is
           user-select: none in the CSS now, which removes the reason it was
           exempt. Real controls still win. */
        if (event.target.closest('a, button')) return;
        g = {
          x: event.clientX,
          y: event.clientY,
          id: event.pointerId,
          axis: null,
          touch: event.pointerType === 'touch',
          /* ⚠️ THE LAST KNOWN TRAVEL AND ITS TIMESTAMP, carried on the gesture
             rather than recomputed at the end. pointerup is not guaranteed to
             arrive — see the pointercancel handler — so the distance already
             covered has to survive the pointer being taken away. */
          dx: 0,
          t0: event.timeStamp,
          t: event.timeStamp,
        };
      });

      stage.addEventListener('pointermove', (event) => {
        if (!g || event.pointerId !== g.id) return;

        const dx = event.clientX - g.x;
        const dy = event.clientY - g.y;

        if (!g.axis) {
          if (Math.abs(dx) < INTENT && Math.abs(dy) < INTENT) return;
          g.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
          /* Vertical is the page scrolling. Drop the candidate and stay out of
             the way — touch-action already handed that axis to the browser. */
          if (g.axis === 'y') {
            g = null;
            return;
          }
          stage.classList.add('is-dragging');

          /* ⚠️ CAPTURE THE POINTER ONCE THE DRAG IS REAL, or a mouse that
             releases outside the picture never delivers pointerup here — and
             the image stays frozen mid-swipe with .is-dragging still on it.
             Touch pointers are captured implicitly by the spec, so this is a
             desktop fix; it is taken AFTER the axis is known so an ordinary
             click is never captured and the side-tap is untouched. */
          if (stage.setPointerCapture) {
            try {
              stage.setPointerCapture(event.pointerId);
            } catch (e) {
              /* Safari throws if the pointer is already gone. Nothing to do. */
            }
          }
        }

        g.dx = dx;
        g.t = event.timeStamp;

        const p = Math.min(Math.abs(dx) / TRAVEL, 1) * PEEK;
        stage.style.setProperty('--drb-pdp-peek', (dx < 0 ? -p : p) + 'px');
      });

      /* ==================================================================
         ⛔ A CANCELLED GESTURE THAT ALREADY TRAVELLED FAR ENOUGH NOW COMMITS
         INSTEAD OF BEING THROWN AWAY.

         Mike, 2026-09-14: "the pull-slide from left to right works great, but
         when you touch and pull from right to left, it rarely works the first
         time."

         [SUPPORTED, not proven — fix verified by Mike on his phone, 2026-09-14:
         "Swipe fixed!" The cause below is still an inference: this change did
         two things at once — honour a cancelled gesture AND accept a flick —
         so either could be what he felt. What the evidence does establish is
         that a DIRECTIONAL failure disappeared when cancelled gestures started
         counting, which is hard to explain any other way. Do not restate it as
         fact, and do not weaken the fix on the grounds that the cause is
         uncertain.]

         A right-to-left swipe on a
         phone starts near the RIGHT edge of the screen, and the picture now
         runs edge to edge. iOS treats an inward drag from either edge as its
         own history gesture — left edge back, right edge forward — and when it
         claims one, the page gets pointercancel instead of pointerup. The old
         handler dropped the gesture on the floor at exactly that moment, which
         is indistinguishable from "the swipe did nothing". Left-to-right
         starting at the left edge is the same conflict, but back-navigation is
         usually available and resolves differently, which fits the asymmetry
         Mike is seeing. It also fits "rarely the FIRST time".

         ⚠️ WHY THIS IS THE RIGHT FIX EVEN IF THE DIAGNOSIS IS WRONG: a
         pointercancel after 40px of deliberate horizontal travel is a gesture
         the person finished and the system interrupted. Honouring it cannot
         produce a slide change nobody asked for, because the same threshold
         governs a normal release.
         ================================================================== */
      stage.addEventListener('pointercancel', () => {
        if (!g) return;
        const commit = g.axis === 'x' && Math.abs(g.dx) >= COMMIT;
        const dir = g.dx < 0 ? 1 : -1;
        g = null;
        if (commit) showSlide(index + dir);
        release();
      });

      stage.addEventListener('pointerup', (event) => {
        if (!g || event.pointerId !== g.id) return;

        const dx = event.clientX - g.x;
        const dy = event.clientY - g.y;
        const dragged = g.axis === 'x';
        const g_t0 = g.t0;
        g = null;

        if (dragged) {
          /* ⚠️ DISTANCE OR SPEED, NOT DISTANCE ALONE. A fast flick covers less
             ground than a deliberate drag before the finger leaves the glass,
             and it is the more common gesture on a phone — 40px of travel is a
             lot to ask of one. FLICK is the floor that stops a twitch counting;
             0.35 px/ms is about 350px a second, which no accidental movement
             reaches. */
          const dt = event.timeStamp - g_t0;
          const speed = dt > 0 ? Math.abs(dx) / dt : 0;
          const far = Math.abs(dx) >= COMMIT;
          const fast = speed >= 0.35 && Math.abs(dx) >= FLICK;
          if (far || fast) showSlide(index + (dx < 0 ? 1 : -1));
          release();
          return;
        }

        release();

        /* ⛔ TAP-TO-ADVANCE IS ON FOR TOUCH HERE, AND THAT IS THE OPPOSITE OF
           THE HERO ON PURPOSE. Mike, 2026-09-13: "just have the left right
           scroll actually work. It doesn't work right now."

           On the hero a stray tap was expensive: it jumped the largest image
           on the site to another product AND stopped the rotation for good.
           This gallery does not rotate and every slide is the same product, so
           the worst a misfire does is show the next photograph — against which
           the alternative was a phone where tapping the picture does nothing
           and no dots remain to suggest otherwise.

           ⚠️ A PAGE SCROLL STILL CANNOT TRIGGER IT. A vertical drag sets the
           axis to y and drops the candidate, so this branch is only reached by
           a press that moved less than 10px in any direction. */
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) {
          const rect = stage.getBoundingClientRect();
          showSlide(index + (event.clientX - rect.left > rect.width / 2 ? 1 : -1));
        }
      });
    }

    /* ---------- purchase options ---------- */
    const options = Array.from(root.querySelectorAll('[data-drb-pdp-option]'));
    const atcPrice = root.querySelector('[data-drb-pdp-atcprice]');

    /* The subscription card's own price line, which the frequency buttons move. */
    const subCard = root.querySelector('.drb-pdp__option--sub');
    const subPriceEl = subCard ? subCard.querySelector('.drb-pdp__prices b') : null;
    const subPerEl = subCard ? subCard.querySelector('.drb-pdp__per') : null;

    /* ⛔ THE SAMPLE MOVED, AND IT HAD TO. It used to be read off the Add to
       Cart button, which stopped printing a price on 2026-09-21. money() falls
       back to a hard-coded '$' with no thousands separator when the sample is
       empty — which is precisely the assumption the sample exists to avoid, and
       it would have silently degraded the per-day figure on line ~362 rather
       than throwing.

       The subscription card's price is rendered by the same Liquid `money`
       filter, so it samples identically. The button is kept as a fallback in
       case a future layout puts the price back there. */
    const sampleEl = subPriceEl || atcPrice;
    const priceSample = sampleEl ? sampleEl.textContent.trim() : '';

    /* ⚠️ The sticky bar reads the SAME write. It is not a second source of
       truth and it is not polled: every path that changes the price already
       goes through here, so the bar cannot drift from the option cards. */
    const stickyPrice = root.querySelector('[data-drb-pdp-stickyprice]');
    const setAtc = (cents) => {
      if (!Number.isFinite(cents)) return;
      const text = money(cents, priceSample);
      if (atcPrice) atcPrice.textContent = text;
      if (stickyPrice) stickyPrice.textContent = text;
    };

    const selectOption = (option) => {
      options.forEach((o) => o.classList.toggle('is-selected', o === option));
      const radio = option.querySelector('.drb-pdp__radio');
      if (!radio) return;
      radio.checked = true;
      setAtc(parseInt(radio.dataset.drbPdpPrice, 10));
    };

    options.forEach((option) => {
      const radio = option.querySelector('.drb-pdp__radio');
      if (!radio) return;
      /* change covers the keyboard; the label element already forwards clicks to
         the radio, so there is no click handler to write. */
      radio.addEventListener('change', () => {
        if (radio.checked) selectOption(option);
      });
    });

    /* ---------- delivery frequency ----------
       ⚠️ CHANGING FREQUENCY ALSO CHANGES WHICH PLAN IS SUBMITTED, not just a
       label. Each button carries its own plan id and its own allocation price,
       so a 60-day plan priced differently from the 30-day one stays correct
       without this file knowing anything about the pricing rules. */
    const freqButtons = Array.from(root.querySelectorAll('[data-drb-pdp-plan]'));
    const subRadio = subCard ? subCard.querySelector('.drb-pdp__radio') : null;
    const days = parseInt(root.dataset.drbPdpDays, 10);

    freqButtons.forEach((button) => {
      button.addEventListener('click', () => {
        freqButtons.forEach((b) => {
          const on = b === button;
          b.classList.toggle('is-on', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });

        const planId = button.dataset.drbPdpPlan;
        const cents = parseInt(button.dataset.drbPdpPlanprice, 10);

        if (subRadio) {
          subRadio.value = planId;
          subRadio.dataset.drbPdpPrice = String(cents);
        }

        if (subPriceEl && Number.isFinite(cents)) {
          subPriceEl.textContent = money(cents, subPriceEl.textContent.trim());
        }

        if (subPerEl && Number.isFinite(cents) && Number.isFinite(days) && days > 0) {
          /* Rebuild only the leading price of the per-shot line and leave the
             rest of the sentence alone, so the copy stays whatever Liquid set. */
          const rest = subPerEl.textContent.replace(/^\s*\S+\s*/, '');
          subPerEl.textContent = money(Math.round(cents / days), priceSample) + ' ' + rest;
        }

        /* Picking a frequency is also picking the subscription. */
        if (subCard) selectOption(subCard);
      });
    });

    /* Reflect whatever the server rendered as checked. */
    const checked = options.find((o) => {
      const r = o.querySelector('.drb-pdp__radio');
      return r && r.checked;
    });
    if (checked) selectOption(checked);
  });
})();

/* ============================================================================
   MOBILE STICKY BUY BAR
   Shows once the real Add to Cart button has scrolled out of view, hides again
   when it comes back.

   ⚠️ IT WATCHES THE BUTTON, NOT A SCROLL POSITION. A pixel threshold would
   need re-tuning for every viewport and would be wrong the moment the buy
   column changed height. "Is the thing this duplicates currently visible" is
   the actual question, and an observer answers it directly.

   ⛔ THE BUTTON IS tabindex="-1" UNTIL THE BAR IS SHOWN. A submit button
   parked off the bottom of every product page is otherwise reachable by
   keyboard, out of context, with no visible focus ring to explain it.
   ============================================================================ */
(function drbPdpSticky() {
  const init = () => {
    document.querySelectorAll('[data-drb-pdp]').forEach((root) => {
      const bar = root.querySelector('[data-drb-pdp-sticky]');
      const atc = root.querySelector('[data-drb-pdp-atc]');
      const btn = root.querySelector('[data-drb-pdp-stickybtn]');
      if (!bar || !atc) return;

      const show = (on) => {
        bar.classList.toggle('is-in', on);
        if (btn) btn.tabIndex = on ? 0 : -1;
      };

      /* ⚠️ No IntersectionObserver means no bar, which is the right failure.
         The page still has its real Add to Cart. */
      if (!('IntersectionObserver' in window)) return;

      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            /* ⚠️ Only once the button is ABOVE the viewport. Before the reader
               has scrolled down to it the button is below, also not
               intersecting, and showing the bar there would put a buy prompt
               over the hero before anyone had read a word. */
            const scrolledPast = e.boundingClientRect.top < 0;
            show(!e.isIntersecting && scrolledPast);
          });
        },
        { threshold: 0, rootMargin: '0px 0px -12px 0px' }
      );
      io.observe(atc);
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
